import { useCallback, useEffect } from "react";
import { useVisitorData } from "@fingerprintjs/fingerprintjs-pro-react";
import { useIsFingerprintEnabled } from "./OptionalFingerprintProvider";
import { reportFingerprintUnavailable } from "./report";

export type FingerprintResult = {
  deviceId: string;
  deviceRequestId: string;
};

export type FingerprintDataType = {
  deviceId: string;
  deviceRequestId: string;
  error: Error | undefined;
  isEnabled: boolean;
  captureData: () => Promise<FingerprintResult>;
};

export const useFingerPrintData = (action: string): FingerprintDataType => {
  const isEnabled = useIsFingerprintEnabled();
  const { getData, data, error } = useVisitorData(
    { extendedResult: true, tag: { userAction: action } },
    { immediate: isEnabled }
  );

  useEffect(() => {
    if (error) {
      reportFingerprintUnavailable(action, "threw", error);
    }
  }, [action, error]);

  const captureData = useCallback(async (): Promise<FingerprintResult> => {
    if (!isEnabled) {
      return { deviceId: "", deviceRequestId: "" };
    }

    try {
      const result = await getData();
      if (!result?.visitorId || !result?.requestId) {
        reportFingerprintUnavailable(action, "no_visitor_data");
        return { deviceId: "", deviceRequestId: "" };
      }
      return {
        deviceId: result.visitorId,
        deviceRequestId: result.requestId,
      };
    } catch (err) {
      console.warn("Fingerprint collection failed:", err);
      return { deviceId: "", deviceRequestId: "" };
    }
  }, [action, getData, isEnabled]);

  return {
    deviceId: data?.visitorId ?? "",
    deviceRequestId: data?.requestId ?? "",
    error,
    isEnabled,
    captureData,
  };
};
